
import { defineComponent } from "vue";
import { useLayoutStore } from "@/stores/layoutStore";
import { useLayerStore } from "@/stores/ssaStore/layer";
import { useSsaStore } from "@/stores/ssaStore";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { useBusyStore } from "@/stores/ssaStore/busy";
import { storeToRefs } from "pinia";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";

export default defineComponent({
	name: "imdex-tool-helper",
	props: {
		active: {
			type: Boolean,
			default: false
		}
	},
	setup() {
		const { panelLayout, toolhelperPos }: any = storeToRefs(useLayoutStore());
		const { SET_TOOLHELPER_POSITION: setToolhelperPos } = useLayoutStore();
		const activeStore = useActiveStore();
		const {
			SSA_ACTIVE_DATASET: activeDataset,
			dataset: datasets,
			view: activeView
		} = storeToRefs(useActiveStore());
		const { hierarchyLayerSelections } = storeToRefs(useHierarchyStore());
		const { SSA_REPRESENTATION_STATES: representationStates, SSA_LAYER_STATES: layerStates } =
			storeToRefs(useLayerStore());
		const { HIERARCHY_STATE: hierarchyState } = storeToRefs(useHierarchyStore());
		const ssaStore = useSsaStore();
		const { BUSY_IS: isBusy } = storeToRefs(useBusyStore());
		const pvwStore = usePvwStore();
		const { PVW_CLIP_ALL: clipAll, PVW_CLIP_UPDATE: updateClip } = usePvwStore();
		const displayPaneStore = useDisplayPaneStore();

		return {
			panelLayout,
			toolhelperPos,
			setToolhelperPos,
			hierarchyState,
			datasets,
			activeView,
			hierarchyLayerSelections,
			updateClip,
			clipAll,
			activeDataset,
			representationStates,
			layerStates,
			ssaStore,
			activeStore,
			pvwStore,
			isBusy,
			displayPaneStore
		};
	},
	data() {
		return {
			dragging: false,
			offsets: [] as any,
			bounds: [] as any,
			boundsH: [] as any,
			toolhelperWidth: 0,
			clearing: false,
			positionMenu: false,
			scaleMenu: false,
			rotationMenu: false,
			easting: 0,
			northing: 0,
			elevation: 0,
			increment: 0,
			lastActiveLayer: "",
			tempScaleX: 0,
			tempScaleY: 0,
			tempScaleZ: 0,
			tempPositionX: 0,
			tempPositionY: 0,
			tempPositionZ: 0,
			tempRotationX: 0,
			tempRotationY: 0,
			tempRotationZ: 0,
			globalBoundsList: {} as any,
			boundsList: {} as any,
			applyToAllEnabled: localStorage.getItem("applyToAllEnabled") === "true"
		};
	},

	computed: {
		allLayerNames() {
			let layers = [] as string[];
			const datasets = Object.keys(
				this.displayPaneStore.DISPLAY_STATES[this.activeView]
			) as string[];
			for (let i = 0; i < datasets.length; i++) {
				const allLayers = this.displayPaneStore.DISPLAY_STATES[this.activeView][
					datasets[i]
				].filter((item: any) => {
					return item.layerName.indexOf("__slice") < 0;
				});

				layers = layers.concat(
					allLayers.map((item: any) => {
						return item.layerName;
					})
				);
			}
			return layers;
		},
		selectedLayerNames() {
			let layers = [] as string[];
			const datasets = Object.keys(
				this.displayPaneStore.DISPLAY_STATES[this.activeView]
			) as string[];
			for (let i = 0; i < datasets.length; i++) {
				const openLayers = this.displayPaneStore.DISPLAY_STATES[this.activeView][
					datasets[i]
				].filter((item: any) => {
					return item.selected;
				});

				layers = layers.concat(
					openLayers.map((item: any) => {
						return item.layerName;
					})
				);
			}
			return layers;
		},
		clipType: {
			get() {
				return (this.activeClip && this.activeClip.type) || "Plane";
			},
			set(v: string) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: { enable: true, visible: true, type: v }
				});
			}
		},
		dip: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.normal == null) return;
				return this.calculateAzimuthDipFromNormal(
					this.activeClip.normal[0],
					this.activeClip.normal[1],
					this.activeClip.normal[2]
				).dip;
			},
			set(v: number) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: { normal: this.calculateNormalVector(this.azimuth, v) }
					}
				});
			}
		},
		azimuth: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.normal == null) return;
				return this.calculateAzimuthDipFromNormal(
					this.activeClip.normal[0],
					this.activeClip.normal[1],
					this.activeClip.normal[2]
				).azimuth;
			},
			set(v: number) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: { normal: this.calculateNormalVector(v, this.dip) }
					}
				});
			}
		},
		rotationX: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.rotation == null) return;
				return this.activeClip.rotation[0];
			},
			set(v: number) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							rotation: [v, this.activeClip.rotation[1], this.activeClip.rotation[2]]
						}
					}
				});
			}
		},
		rotationY: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.rotation == null) return;
				return this.activeClip.rotation[1];
			},
			set(v: number) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							rotation: [this.activeClip.rotation[0], v, this.activeClip.rotation[2]]
						}
					}
				});
			}
		},
		rotationZ: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.rotation == null) return;
				return this.activeClip.rotation[2];
			},
			set(v: number) {
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							rotation: [this.activeClip.rotation[0], this.activeClip.rotation[1], v]
						}
					}
				});
			}
		},
		boxSize() {
			if (this.activeClip.bounds != null) {
				return {
					width: this.activeClip.bounds[1] * this.activeClip.length[0],
					height: this.activeClip.bounds[3] * this.activeClip.length[1],
					depth: this.activeClip.bounds[5] * this.activeClip.length[2],
					original_width: this.activeClip.bounds[1],
					original_height: this.activeClip.bounds[3],
					original_depth: this.activeClip.bounds[5]
				};
			} else {
				return {
					width: 0,
					height: 0,
					depth: 0,
					original_width: 0,
					original_height: 0,
					original_depth: 0
				};
			}
		},

		scaleX: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				return (
					(this.boxSize.original_width - this.activeClip.bounds[0]) *
					this.activeClip.length[0]
				);
			},
			set(v: number) {
				this.boundsList[this.activeLayer][0] =
					v / (this.boxSize.original_width - this.activeClip.bounds[0]);
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							length: [
								v / (this.boxSize.original_width - this.activeClip.bounds[0]),
								this.activeClip.length[1],
								this.activeClip.length[2]
							]
						}
					}
				});
			}
		},
		scaleY: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				return (
					(this.boxSize.original_height - this.activeClip.bounds[2]) *
					this.activeClip.length[1]
				);
			},
			set(v: number) {
				this.boundsList[this.activeLayer][1] =
					v / (this.boxSize.original_height - this.activeClip.bounds[2]);
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							length: [
								this.activeClip.length[0],
								v / (this.boxSize.original_height - this.activeClip.bounds[2]),
								this.activeClip.length[2]
							]
						}
					}
				});
			}
		},
		scaleZ: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				return (
					(this.boxSize.original_depth - this.activeClip.bounds[4]) *
					this.activeClip.length[2]
				);
			},
			set(v: number) {
				this.boundsList[this.activeLayer][2] =
					v / (this.boxSize.original_depth - this.activeClip.bounds[4]);
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							length: [
								this.activeClip.length[0],
								this.activeClip.length[1],
								v / (this.boxSize.original_depth - this.activeClip.bounds[4])
							]
						}
					}
				});
			}
		},
		positionX: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				const dif = (this.boxSize.original_width - this.activeClip.bounds[0]) / 2; // gets the difference between the original width and the current width
				return (
					dif * this.activeClip.length[0] +
					(this.activeClip.position[0] / this.activeClip.length[0] +
						this.activeClip.bounds[0]) *
						this.activeClip.length[0] -
					this.activeClip.positionOffset[0]
				);
			},
			set(v: number) {
				const dif = (this.boxSize.original_width - this.activeClip.bounds[0]) / 2;
				const scaleFactor = this.activeClip.length[0];
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							position: [
								v +
									this.activeClip.positionOffset[0] -
									(this.activeClip.bounds[0] + dif) * scaleFactor,
								this.activeClip.position[1],
								this.activeClip.position[2]
							]
						}
					}
				});
				if (this.applyToAllEnabled) {
					this.pvwStore.PVW_CLIP_TO_LAYERS({
						layerName: this.activeLayer,
						layersToClip: this.selectedLayerNames,
						viewId: this.activeView
					});
				}
			}
		},
		positionY: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				const dif = (this.boxSize.original_height - this.activeClip.bounds[2]) / 2;
				return (
					dif * this.activeClip.length[1] +
					(this.activeClip.position[1] / this.activeClip.length[1] +
						this.activeClip.bounds[2]) *
						this.activeClip.length[1] -
					this.activeClip.positionOffset[1]
				);
			},
			set(v: number) {
				const dif = (this.boxSize.original_height - this.activeClip.bounds[2]) / 2;
				const scaleFactor = this.activeClip.length[1];
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							position: [
								this.activeClip.position[0],
								v +
									this.activeClip.positionOffset[1] -
									(this.activeClip.bounds[2] + dif) * scaleFactor,
								this.activeClip.position[2]
							]
						}
					}
				});
				if (this.applyToAllEnabled) {
					this.pvwStore.PVW_CLIP_TO_LAYERS({
						layerName: this.activeLayer,
						layersToClip: this.selectedLayerNames,
						viewId: this.activeView
					});
				}
			}
		},
		positionZ: {
			get(): any {
				if (this.activeClip == null) return;
				if (this.activeClip.bounds == null) return;
				const dif = (this.boxSize.original_depth - this.activeClip.bounds[4]) / 2;
				return (
					dif * this.activeClip.length[2] +
					(this.activeClip.position[2] / this.activeClip.length[2] +
						this.activeClip.bounds[4]) *
						this.activeClip.length[2] -
					this.activeClip.positionOffset[2]
				);
			},
			set(v: number) {
				const scaleFactor = this.activeClip.length[2];
				const dif = (this.boxSize.original_depth - this.activeClip.bounds[4]) / 2;
				this.updateClip({
					layerName: this.activeLayer,
					viewId: this.activeView,
					options: {
						enable: true,
						visible: true,
						action: {
							position: [
								this.activeClip.position[0],
								this.activeClip.position[1],
								v +
									this.activeClip.positionOffset[2] -
									(this.activeClip.bounds[4] + dif) * scaleFactor
							]
						}
					}
				});
				if (this.applyToAllEnabled) {
					this.pvwStore.PVW_CLIP_TO_LAYERS({
						layerName: this.activeLayer,
						layersToClip: this.selectedLayerNames,
						viewId: this.activeView
					});
				}
			}
		},
		activeClip(): any {
			return (
				this.layerStates[this.activeView] &&
				this.layerStates[this.activeView][this.activeLayer] &&
				this.layerStates[this.activeView][this.activeLayer].clip
			);
		},
		activeLayer(): any {
			return (
				(this.activeStore.SSA_ACTIVE_LAYER &&
					this.activeStore.SSA_ACTIVE_LAYER.split("__slice")[0]) ||
				""
			);
		},
		hasDataLoaded(): any {
			return (
				this.activeDataset &&
				this.hierarchyState[this.activeView] &&
				this.hierarchyState[this.activeView].selected.filter((item: any) =>
					item.includes(this.activeDataset.id)
				).length
			);
		},
		middleId(): any {
			return (
				(this.panelLayout.children[
					this.panelLayout.children.findIndex(
						(item: any) => item.componentName == "MIDDLE"
					)
				] &&
					this.panelLayout.children[
						this.panelLayout.children.findIndex(
							(item: any) => item.componentName == "MIDDLE"
						)
					].id) ||
				null
			);
		}
	},

	watch: {
		activeLayer(v, o) {
			this.hide();
		},
		active(v) {
			if (v) {
				this.init();

				this.clearing = false;
				this.$nextTick(() => {
					this.lastActiveLayer = JSON.parse(JSON.stringify(this.activeLayer));
				});
			} else {
				this.$nextTick(() => {
					this.updateClip({
						layerName: this.lastActiveLayer,
						viewId: this.activeView,
						options: { enable: this.clearing ? false : true, visible: false }
					});
					this.$nextTick(() => {
						// setTimeout(()=>{
						// 	this.lastActiveLayer = "";
						// },100)
					});
				});
			}
		},
		hierarchyLayerSelections: {
			deep: true,
			handler(v) {
				// this.lastActiveLayer =
				// 	this.activeLayer == ""
				// 		? this.lastActiveLayer
				// 		: JSON.parse(JSON.stringify(this.activeLayer));

				if (this.hasDataLoaded) {
					// this.clear();
				} else {
					this.clear();
				}
			}
		}
	},
	methods: {
		resetOrigin() {
			const dif1 = (this.boxSize.original_width - this.activeClip.bounds[0]) / 2;
			const dif2 = (this.boxSize.original_height - this.activeClip.bounds[2]) / 2;
			const dif3 = (this.boxSize.original_depth - this.activeClip.bounds[4]) / 2;
			const scaleFactorX = this.activeClip.length[0];
			const scaleFactorY = this.activeClip.length[1];
			const scaleFactorZ = this.activeClip.length[2];
			this.updateClip({
				layerName: this.activeLayer,
				viewId: this.activeView,
				options: {
					enable: true,
					visible: true,
					action: {
						position: [
							this.activeClip.bounds[0] +
								dif1 -
								(this.activeClip.bounds[0] + dif1) * scaleFactorX,
							this.activeClip.bounds[2] +
								dif2 -
								(this.activeClip.bounds[2] + dif2) * scaleFactorY,
							this.activeClip.bounds[4] +
								dif3 -
								(this.activeClip.bounds[4] + dif3) * scaleFactorZ
						]
					}
				}
			});
		},
		incrementDown(key: string) {
			(this as any)[key] = (this as any)[key] - this.increment;
		},
		incrementUp(key: string) {
			(this as any)[key] = (this as any)[key] + this.increment;
		},
		applyToAll() {
			this.applyToAllEnabled = !this.applyToAllEnabled;
			localStorage.setItem("applyToAllEnabled", this.applyToAllEnabled.toString());

			if (this.applyToAllEnabled) {
				this.pvwStore.PVW_CLIP_TO_LAYERS({
					layerName: this.activeLayer,
					layersToClip: this.selectedLayerNames,
					viewId: this.activeView
				});
			}
		},
		toggleClipType() {
			if (this.clipType == "Plane") {
				this.clipType = "Box";
			} else {
				this.clipType = "Plane";
			}
		},
		init() {
			if (this.toolhelperPos.length == 0) {
				const el: any = document.getElementById(this.middleId);
				const toolhelper: any = this.$refs.toolhelper;
				const { width: twidth } = toolhelper.getBoundingClientRect();
				const {
					width,
					height,
					x,
					y
				}: { width: number; height: number; x: number; y: number } =
					el.getBoundingClientRect();
				localStorage.setItem(
					"toolhelper-position",
					`[${x + width / 2 - twidth / 2},${height / 3 + y}]`
				);
				this.setToolhelperPos([x + width / 2 - twidth / 2, height / 3 + y]);
			}
			this.addClip();
		},
		invertClip() {
			this.updateClip({
				layerName: this.activeLayer,
				viewId: this.activeView,
				options: {
					enable: true,
					visible: true,
					action: {
						normal: [
							Number(this.activeClip.normal[0]) * -1,
							Number(this.activeClip.normal[1]) * -1,
							Number(this.activeClip.normal[2]) * -1
						]
					}
				}
			});
		},
		async clear() {
			if (this.lastActiveLayer === "") {
				console.log(`lastActiveLayer === "" [likely loading save state]`);
				return;
			}
			this.clearing = true;
			this.applyToAllEnabled = false;

			this.updateClip({
				layerName: this.lastActiveLayer,
				viewId: this.activeView,
				options: {
					enable: false,
					visible: false
				}
			});

			// this.updateClip({
			// 	layerName: this.lastActiveLayer,
			// 	viewId: this.activeView,
			// 	options: { enable: false, visible: false }
			// });
			// setTimeout(() => {
			this.pvwStore.PVW_CLIP_TO_LAYERS({
				layerName: this.activeLayer,
				layersToClip: this.allLayerNames,
				viewId: this.activeView,
				options: {
					enable: false,
					visible: false
				}
			});
			// }, 300);
			this.$emit("clearClip");
		},
		async hide() {
			this.$emit("clearClip");
		},
		async reset() {
			// this.positionX = this.original_width;
			// this.positionY = this.original_height;
			// this.positionZ = this.original_depth;
			// this.tempScaleX = this.globalBoundsList[this.activeLayer][0];
			// this.tempScaleY = this.globalBoundsList[this.activeLayer][1];
			// this.tempScaleZ = this.globalBoundsList[this.activeLayer][2];

			// this.updateClip({
			// 		layerName: this.activeLayer,
			// 		viewId: this.activeView,
			// 		options: {
			// 			enable: true,
			// 			visible: true,
			// 			action: {
			// 				length: [
			// 					this.globalBoundsList[this.activeLayer][0] / (this.boxSize.original_width - this.activeClip.bounds[0]),
			// 					this.globalBoundsList[this.activeLayer][1] / (this.boxSize.original_height - this.activeClip.bounds[2]),
			// 					this.globalBoundsList[this.activeLayer][2] / (this.boxSize.original_depth - this.activeClip.bounds[4]),
			// 				]
			// 			}
			// 		}
			// 	});
			//
			// this.scaleZ = this.globalBoundsList[this.activeLayer][2];
			this.updateClip({
				layerName: this.activeLayer,
				viewId: this.activeView,
				options: {
					enable: true,
					visible: true,
					action: { reset: true }
				}
			});
		},
		async addClip() {
			this.updateClip({
				layerName: this.activeLayer,
				viewId: this.activeView,
				options: { enable: true, visible: true, type: this.clipType }
			});
			this.$nextTick(() => {
				setTimeout(() => {
					this.globalBoundsList[this.activeLayer] = [
						this.scaleX,
						this.scaleY,
						this.scaleZ
					];
					this.boundsList[this.activeLayer] = [this.scaleX, this.scaleY, this.scaleZ];
				}, 500);
			});
		},
		async removeClip(id: number) {
			this.updateClip({
				layerName: this.activeLayer,
				viewId: this.activeView,
				options: { enable: false, visible: true, type: this.clipType }
			});
			setTimeout(() => {
				this.pvwStore.PVW_CLIP_TO_LAYERS({
					layerName: this.activeLayer,
					layersToClip: this.allLayerNames,
					viewId: this.activeView
				});
			}, 300);
		},
		calculateNormalVector(azimuth: number, dip: number) {
			// Helper function to convert dip/azi to x, y, z normal
			const azimuthRad = (Math.PI / 180) * Number(azimuth);
			const dipRad = (Math.PI / 180) * (90 + Number(dip));

			const x = Math.cos(dipRad) * Math.sin(azimuthRad);
			const y = Math.cos(dipRad) * Math.cos(azimuthRad);
			const z = Math.sin(dipRad);
			return [x, y, z];
		},
		calculateAzimuthDipFromNormal(x: number, y: number, z: number) {
			// Helper function to convert x, y, z normal back to azimuth and dip
			const azimuth = Math.atan2(x, y) * (180 / Math.PI);
			const hypotenuse = Math.sqrt(x * x + y * y);
			const dip = Math.atan2(z, hypotenuse) * (180 / Math.PI) - 90;

			// Adjusting azimuth to be between 0 and 360 degrees
			const adjustedAzimuth = azimuth < 0 ? 360 + azimuth : azimuth;

			return {
				azimuth: adjustedAzimuth,
				dip: dip
			};
		},
		dragStart(e: any) {
			const { clientX: x, clientY: y, offsetX: ox, offsetY: oy } = e;
			this.dragging = true;
			this.offsets = [ox + 5, oy + 5];
			const el: HTMLElement = (this.$refs as any).toolhelper;
			const middleEl: any = document.getElementById(this.middleId);
			const {
				width: w2,
				height: h2,
				x: x2,
				y: y2
			}: {
				width: number;
				height: number;
				y: number;
				x: number;
			} = middleEl.getBoundingClientRect();
			this.bounds = [x2, x2 + w2];
			this.boundsH = [y2, y2 + h2];
			const { width } = el.getBoundingClientRect();
			this.toolhelperWidth = width;
			el.classList.add("dragging");
			const bodyEl: any = document.getElementsByTagName("body")[0];
			bodyEl.style.cursor = "grabbing !important";
			el.style.top = y - this.offsets[1] + "px";
			el.style.left = x - this.offsets[0] + "px";
			window.addEventListener("mouseup", this.endDrag);
			window.addEventListener("mousemove", this.moveMouse);
		},
		moveMouse(e: any) {
			const { clientX: x, clientY: y } = e;
			const el: HTMLElement = (this.$refs as any).toolhelper;
			const { x: tx, width: tw, height: th, y: ty } = el.getBoundingClientRect();
			let xx = x - this.offsets[0];
			let yy = y - this.offsets[1];
			if (x < this.bounds[0]) xx = this.bounds[0];
			if (x + tw > this.bounds[1]) xx = this.bounds[1] - tw;
			if (y < this.boundsH[0]) yy = this.boundsH[0];
			if (y + th > this.boundsH[1]) yy = this.boundsH[1] - th;

			el.style.top = yy + "px";
			el.style.left = xx + "px";
		},
		endDrag(e: any) {
			this.dragging = false;
			const el: HTMLElement = (this.$refs as any).toolhelper;
			el.classList.remove("dragging");
			const bodyEl: any = document.getElementsByTagName("body")[0];
			bodyEl.style.cursor = "default";
			window.removeEventListener("mousemove", this.moveMouse);
			window.removeEventListener("mouseup", this.endDrag);
		}
	}
});
